import { Link, Navigate, useParams } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { useAuthStore } from '@/store';
import { NAV_HUBS } from '@/config/navHubs';

// Renders a section "hub": the sub-links of a sidebar section as clickable
// cards. Reached via a single sidebar link (e.g. /hub/settings). An unknown
// hubId redirects to /dashboard, which is what retires a hub safely: the
// quotes hub was removed in favour of linking straight to /quotes, and any
// bookmarked /hub/quotes degrades to the dashboard rather than 404ing.
export function NavHubPage() {
  const { hubId } = useParams<{ hubId: string }>();
  const { user } = useAuthStore();
  const role = user?.role || '';

  const hub = hubId ? NAV_HUBS[hubId] : undefined;
  if (!hub) return <Navigate to="/dashboard" replace />;

  const links = hub.links.filter((l) => !l.roles || l.roles.includes(role));

  return (
    <div>
      <PageHeader title={hub.title} subtitle={hub.subtitle} />

      {links.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nothing available here for your role.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {links.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="group flex items-start gap-4 p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-400 hover:shadow-sm transition-colors"
            >
              <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                <link.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{link.name}</p>
                  <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-primary-500 flex-shrink-0" />
                </div>
                {link.description && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{link.description}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default NavHubPage;
